'use client'

import { Menu as BaseMenu } from '@base-ui/react/menu'
import * as stylex from '@stylexjs/stylex'
import type { ComponentPropsWithRef, HTMLAttributes, ReactElement, ReactNode, RefAttributes, RefObject } from 'react'

import { useMobileSheet } from '../mobile-sheet'
import { mobileSheetPosition, type ControlledOpenState, type OverlayPositionerProps } from '../overlay'
import { overlayStyles } from '../overlay'
import { colors, fonts, fontSizes, radii, spacing } from '../tokens/tokens.stylex'

const styles = stylex.create({
  font: { fontFamily: fonts.interface },
  item: {
    alignItems: 'center',
    backgroundColor: { default: 'transparent', ':is([data-highlighted])': colors.hover },
    borderRadius: radii.small,
    color: { default: colors.foreground, ':is([data-disabled])': colors.muted },
    cursor: { default: 'pointer', ':is([data-disabled])': 'not-allowed' },
    display: 'flex',
    fontFamily: fonts.interface,
    fontSize: fontSizes.label,
    gap: spacing.space8,
    minHeight: 40,
    outline: { default: 'none', ':focus-visible': `2px solid ${colors.foreground}` },
    outlineOffset: -2,
    paddingBlock: spacing.space8,
    paddingInline: spacing.space12,
    textDecoration: 'none',
  },
  muted: { color: colors.muted },
})

type MenuContent = { children: ReactNode; modal?: boolean }

export type MenuProps = MenuContent & ControlledOpenState

/**
 * Use for a list of actions; grouped site destinations belong in NavigationMenu. Applications supply labels and
 * actions, while the menu owns keyboard selection and focus return. Controlled menus pair open with onOpenChange
 * and omit defaultOpen. Status: supported.
 */
export function Menu({ onOpenChange, ...props }: MenuProps) {
  return <BaseMenu.Root {...props} onOpenChange={onOpenChange} />
}

type MenuTriggerOptions = {
  variant?: 'plain' | 'row'
}

export type MenuTriggerProps = Omit<ComponentPropsWithRef<'button'>, 'className' | 'style' | 'color'> &
  MenuTriggerOptions

/**
 * Use within Menu to open its action list; use Button for an action without a menu. Applications provide an
 * accessible name and choose plain or row composition. Status: supported.
 */
export function MenuTrigger({ variant = 'plain', ...props }: MenuTriggerProps) {
  return (
    <BaseMenu.Trigger
      {...props}
      {...stylex.props(overlayStyles.trigger, styles.font, variant === 'row' && overlayStyles.row)}
    />
  )
}

export type MenuPortalProps = { children: ReactNode }

/**
 * Use with MenuPositioner and MenuPopup to render an action menu outside clipping containers. The portal
 * inherits the document theme, so applications set their theme on the document root. Use PopoverPortal for
 * contextual content. Status: supported.
 */
export function MenuPortal({ children }: MenuPortalProps) {
  return <BaseMenu.Portal>{children}</BaseMenu.Portal>
}

export type MenuPositionerProps = OverlayPositionerProps

/**
 * Use to anchor MenuPopup to its trigger while preserving collision placement. Applications choose placement;
 * mobileSheet must be enabled on both the positioner and popup for the coarse-pointer sheet. Use
 * PopoverPositioner for contextual content. Status: supported.
 */
export function MenuPositioner({ mobileSheet = false, sideOffset = 8, ...props }: MenuPositionerProps) {
  const sheet = useMobileSheet(mobileSheet)

  return (
    <BaseMenu.Positioner
      {...props}
      sideOffset={sideOffset}
      collisionPadding={8}
      {...stylex.props(overlayStyles.positioner)}
      style={sheet ? mobileSheetPosition : undefined}
    />
  )
}

type MenuPopupOptions = {
  finalFocus?: boolean | RefObject<HTMLElement | null> | (() => HTMLElement | null)
  size?: 'default' | 'compact'
  mobileSheet?: boolean
}

export type MenuPopupProps = Omit<ComponentPropsWithRef<'div'>, 'className' | 'style' | 'color'> & MenuPopupOptions

/**
 * Use for the surface containing MenuItem actions; choose PopoverPopup for contextual controls. Applications
 * provide the actions and keep mobileSheet consistent with MenuPositioner. The menu manages keyboard focus and
 * dismissal. Status: supported.
 */
export function MenuPopup({ size = 'default', mobileSheet = false, ...props }: MenuPopupProps) {
  const sheet = useMobileSheet(mobileSheet)

  return (
    <BaseMenu.Popup
      {...props}
      {...stylex.props(
        overlayStyles.popup,
        styles.font,
        size === 'compact' && overlayStyles.compact,
        sheet && overlayStyles.sheet
      )}
    />
  )
}

type MenuItemOptions = {
  disabled?: boolean
  closeOnClick?: boolean
  render?: ReactElement
  variant?: 'normal' | 'muted'
}

export type MenuItemProps = Omit<HTMLAttributes<HTMLElement>, 'className' | 'style' | 'color'> &
  RefAttributes<HTMLElement> &
  MenuItemOptions

/**
 * Use for one action within MenuPopup; choose Link for an independent destination outside a menu. Applications
 * provide its label and activation handler, and decide disabled and close-on-click behavior. Status: supported.
 */
export function MenuItem({ variant = 'normal', ...props }: MenuItemProps) {
  return <BaseMenu.Item {...props} {...stylex.props(styles.item, variant === 'muted' && styles.muted)} />
}
